import React from 'react';
import { FaTimes, FaInbox, FaExclamationCircle } from 'react-icons/fa';

/** Brand accent used across the attendance screens. */
export const ACCENT = '#15A857';

export const PageShell = ({ title, subtitle, actions, children }) => (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 pt-20 dark:from-[#05070d] dark:to-[#111827]">
        <div className="container mx-auto px-4 sm:px-6 py-8">
            <div className="mb-8 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                <div>
                    <h1 className="text-3xl sm:text-4xl font-bold text-gray-800 dark:text-slate-200">{title}</h1>
                    {subtitle && <p className="text-gray-600 mt-1 dark:text-slate-300">{subtitle}</p>}
                </div>
                {actions && <div className="flex flex-wrap items-center gap-3">{actions}</div>}
            </div>
            {children}
        </div>
    </div>
);

export const Card = ({ title, action, className = '', children }) => (
    <div className={`bg-white rounded-2xl shadow-lg p-6 dark:bg-[#111827] dark:shadow-black/50 ${className}`}>
        {(title || action) && (
            <div className="flex items-center justify-between mb-5">
                {title && <h2 className="text-xl font-bold text-gray-800 dark:text-slate-100">{title}</h2>}
                {action}
            </div>
        )}
        {children}
    </div>
);

export const StatCard = ({ icon, label, value, hint, tone = 'green', onClick }) => {
    const tones = {
        green: 'bg-gradient-to-br from-emerald-50 to-green-100 text-emerald-600 dark:from-emerald-500/15 dark:to-emerald-500/5 dark:text-emerald-300',
        blue: 'bg-gradient-to-br from-sky-50 to-blue-100 text-blue-600 dark:from-sky-500/15 dark:to-blue-500/5 dark:text-sky-300',
        red: 'bg-gradient-to-br from-rose-50 to-red-100 text-red-600 dark:from-rose-500/15 dark:to-red-500/5 dark:text-rose-300',
        amber: 'bg-gradient-to-br from-amber-50 to-yellow-100 text-amber-600 dark:from-amber-500/15 dark:to-yellow-500/5 dark:text-amber-300',
        purple: 'bg-gradient-to-br from-violet-50 to-purple-100 text-violet-600 dark:from-violet-500/15 dark:to-purple-500/5 dark:text-violet-300',
        gray: 'bg-gradient-to-br from-slate-100 to-slate-200 text-slate-600 dark:from-slate-700 dark:to-slate-800 dark:text-slate-200',
    };
    const Tag = onClick ? 'button' : 'div';
    return (
        <Tag
            onClick={onClick}
            className={`bg-white rounded-2xl shadow-lg p-6 text-left w-full dark:bg-[#0b1220] dark:shadow-black/50 ${onClick ? 'hover:shadow-xl transition-shadow cursor-pointer' : ''}`}
        >
            <div className="flex items-center justify-between mb-3">
                <span className="text-gray-500 font-medium text-sm dark:text-slate-300">{label}</span>
                <span className={`w-10 h-10 rounded-xl flex items-center justify-center ${tones[tone] || tones.gray}`}>
                    {icon}
                </span>
            </div>
            <div className="text-3xl font-bold text-gray-800 dark:text-slate-100">{value}</div>
            {hint && <div className="text-xs text-gray-500 mt-1 dark:text-slate-400">{hint}</div>}
        </Tag>
    );
};

const BADGE_TONES = {
    present: 'bg-green-100 text-green-700',
    late: 'bg-amber-100 text-amber-700',
    half_day: 'bg-orange-100 text-orange-700',
    absent: 'bg-red-100 text-red-700',
    on_leave: 'bg-purple-100 text-purple-700',
    holiday: 'bg-blue-100 text-blue-700',
    weekend: 'bg-gray-100 text-gray-600',
    no_record: 'bg-gray-100 text-gray-500',
    pending: 'bg-amber-100 text-amber-700',
    submitted: 'bg-blue-100 text-blue-700',
    approved: 'bg-green-100 text-green-700',
    rejected: 'bg-red-100 text-red-700',
    cancelled: 'bg-gray-100 text-gray-600',
    draft: 'bg-gray-100 text-gray-600',
    online: 'bg-green-100 text-green-700',
    offline: 'bg-red-100 text-red-700',
    syncing: 'bg-blue-100 text-blue-700',
    error: 'bg-red-100 text-red-700',
    active: 'bg-green-100 text-green-700',
    inactive: 'bg-gray-100 text-gray-600',
    suspended: 'bg-amber-100 text-amber-700',
    terminated: 'bg-red-100 text-red-700',
};

export const STATUS_LABELS = {
    present: 'Present',
    late: 'Late',
    half_day: 'Half Day',
    absent: 'Absent',
    on_leave: 'On Leave',
    holiday: 'Holiday',
    weekend: 'Weekend',
    no_record: 'No Record',
};

export const Badge = ({ value, label }) => (
    <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold capitalize ${BADGE_TONES[value] || 'bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-slate-200'}`}>
        {label || STATUS_LABELS[value] || String(value || '').replace(/_/g, ' ')}
    </span>
);

export const Button = ({ variant = 'primary', className = '', children, ...rest }) => {
    const variants = {
        primary: 'bg-[#15A857] hover:bg-green-700 text-white shadow-lg shadow-[#15A857]/20',
        secondary: 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100 dark:hover:bg-slate-700',
        danger: 'bg-red-500 hover:bg-red-600 text-white',
        subtle: 'bg-gray-100 hover:bg-gray-200 text-gray-700 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700',
    };
    return (
        <button
            className={`px-4 py-2.5 rounded-xl font-semibold transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2 ${variants[variant]} ${className}`}
            {...rest}
        >
            {children}
        </button>
    );
};

export const Field = ({ label, hint, children }) => (
    <label className="block">
        <span className="block text-sm font-semibold text-gray-700 mb-1.5 dark:text-slate-200">{label}</span>
        {children}
        {hint && <span className="block text-xs text-gray-500 mt-1 dark:text-slate-400">{hint}</span>}
    </label>
);

export const inputClass =
    'w-full px-3 py-2.5 bg-white border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#15A857] focus:border-[#15A857] outline-none transition-all text-gray-800 dark:bg-slate-900 dark:border-slate-700 dark:text-slate-200 dark:placeholder:text-slate-400';

export const Modal = ({ isOpen, onClose, title, size = 'max-w-3xl', children }) => {
    if (!isOpen) return null;
    return (
        <div
            className="fixed inset-0 bg-black bg-opacity-40 z-[60] flex items-center justify-center p-4 backdrop-blur-sm"
            onClick={(event) => { if (event.target === event.currentTarget) onClose(); }}
        >
            <div className={`bg-white rounded-2xl w-full ${size} max-h-[90vh] flex flex-col overflow-hidden shadow-2xl`}>
                <div className="p-5 border-b flex justify-between items-center bg-gray-50">
                    <h2 className="text-xl font-bold text-gray-800">{title}</h2>
                    <button
                        onClick={onClose}
                        className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors"
                        aria-label="Close"
                    >
                        <FaTimes />
                    </button>
                </div>
                <div className="p-6 overflow-y-auto flex-1">{children}</div>
            </div>
        </div>
    );
};

export const Table = ({ columns, rows, renderRow, empty = 'Nothing to show yet.' }) => (
    <div className="overflow-x-auto -mx-6 px-6">
        <table className="w-full min-w-[640px] text-sm">
            <thead>
                <tr className="text-left text-gray-500 border-b dark:border-slate-700 dark:text-slate-300">
                    {columns.map((column) => (
                        <th key={column} className="py-3 pr-4 font-semibold whitespace-nowrap">{column}</th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {rows.length === 0 ? (
                    <tr>
                        <td colSpan={columns.length} className="py-10 text-center text-gray-500 dark:text-slate-400">
                            {empty}
                        </td>
                    </tr>
                ) : rows.map(renderRow)}
            </tbody>
        </table>
    </div>
);

export const Spinner = ({ className = 'h-8 w-8' }) => (
    <div className={`animate-spin rounded-full border-2 border-gray-200 border-t-[#15A857] ${className}`} />
);

export const Loading = ({ label = 'Loading…' }) => (
    <div className="flex flex-col items-center justify-center py-16 gap-3 text-gray-500">
        <Spinner />
        <span>{label}</span>
    </div>
);

export const EmptyState = ({ icon, title, hint }) => (
    <div className="flex flex-col items-center justify-center py-14 text-center text-gray-500">
        <div className="text-4xl text-gray-300 mb-3">{icon || <FaInbox />}</div>
        <div className="font-semibold text-gray-700">{title}</div>
        {hint && <div className="text-sm mt-1 max-w-sm">{hint}</div>}
    </div>
);

export const Alert = ({ tone = 'error', children }) => {
    if (!children) return null;
    const tones = {
        error: 'bg-red-50 text-red-700 border-red-200 dark:bg-red-950/30 dark:text-red-200 dark:border-red-800/80',
        success: 'bg-green-50 text-green-700 border-green-200 dark:bg-green-950/30 dark:text-green-200 dark:border-green-800/80',
        info: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/30 dark:text-blue-200 dark:border-blue-800/80',
        warning: 'bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-950/30 dark:text-amber-200 dark:border-amber-800/80',
    };
    return (
        <div className={`flex items-start gap-2 p-3 border rounded-xl text-sm mb-4 ${tones[tone]}`}>
            <FaExclamationCircle className="mt-0.5 flex-shrink-0" />
            <span>{children}</span>
        </div>
    );
};

/** Horizontal bar showing an attendance percentage. */
export const RateBar = ({ value }) => (
    <div className="flex items-center gap-3">
        <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden min-w-[60px] dark:bg-slate-700">
            <div
                className="h-full rounded-full"
                style={{
                    width: `${Math.max(0, Math.min(100, value))}%`,
                    backgroundColor: value >= 90 ? ACCENT : value >= 75 ? '#F59E0B' : '#EF4444',
                }}
            />
        </div>
        <span className="text-sm font-semibold text-gray-700 w-12 text-right dark:text-slate-200">{value}%</span>
    </div>
);

// -- formatting helpers ----------------------------------------------------

export const formatTime = (value) => {
    if (!value) return '--:--';
    return new Date(value).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
};

export const formatDate = (value, options) => {
    if (!value) return '—';
    const date = value.length === 10 ? new Date(`${value}T00:00:00`) : new Date(value);
    return date.toLocaleDateString('en-GB',
        options || { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
};

export const formatDateTime = (value) => {
    if (!value) return '—';
    return new Date(value).toLocaleString('en-GB', {
        day: 'numeric', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });
};

export const todayISO = () => new Date().toLocaleDateString('en-CA');

export const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];
